import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Search, 
  Filter,
  Plus,
  FolderOpen,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Download,
  FileSpreadsheet,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { isRestrictedUser, getUserRole } from "./Login";
import { SortHeader } from "@/components/ui/sort-header";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const PROFILE_LABELS = {
  bailleur: "Bailleur",
  proprietaire_occupant: "Propriétaire",
  sci: "SCI"
};

const STATUS_CONFIG = {
  complete: { label: "Complet", icon: CheckCircle2, color: "text-[hsl(145,100%,39%)]", bg: "bg-[hsl(145,100%,45%)]/10", border: "border-[hsl(145,100%,45%)]/20" },
  in_progress: { label: "En cours", icon: Clock, color: "text-[hsl(187,71%,50%)]", bg: "bg-[hsl(187,71%,59%)]/10", border: "border-[hsl(187,71%,59%)]/20" },
  incomplete: { label: "Incomplet", icon: AlertTriangle, color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20" }
};

const PRECARITE_CONFIG = {
  BLEU: { label: "Bleu", color: "bg-blue-500" },
  JAUNE: { label: "Jaune", color: "bg-yellow-500/100" },
  VIOLET: { label: "Violet", color: "bg-purple-500/100" }
};

const formatCurrency = (amount) => {
  if (!amount) return "0 €";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Get next step name (first non-done step)
const getNextStepName = (steps) => {
  if (!steps || steps.length === 0) return "-";
  const nextStep = steps.find(s => s.status !== 'done');
  return nextStep ? nextStep.name : "Terminé";
};

// Get last validated (done) step name — for display as "statut"
const getLastDoneStepName = (steps) => {
  if (!steps || steps.length === 0) return "-";
  const doneSteps = steps.filter(s => s.status === 'done');
  if (doneSteps.length === 0) return "Non démarré";
  return doneSteps[doneSteps.length - 1].name;
};

// Get last validated (done) step key — for filtering by "statut"
const getLastDoneStepKey = (steps) => {
  if (!steps || steps.length === 0) return "non_demarre";
  const doneSteps = steps.filter(s => s.status === 'done');
  if (doneSteps.length === 0) return "non_demarre";
  // All done => terminé
  const allDone = steps.every(s => s.status === 'done');
  if (allDone) return "termine";
  return doneSteps[doneSteps.length - 1].key;
};

// Get next step order for sorting
const getNextStepOrder = (steps) => {
  if (!steps || steps.length === 0) return 999;
  const nextStep = steps.find(s => s.status !== 'done');
  return nextStep ? nextStep.order : 999; // 999 = terminé (à la fin)
};

// Étapes MPR pour tri et filtre
const STEPS_MPR = [
  { key: "devis_signe", name: "Devis signé", order: 1 },
  { key: "compte_mpr", name: "Compte MPR", order: 2 },
  { key: "dossier_valide", name: "Dossier validé Ynergie", order: 3 },
  { key: "dossier_octroye", name: "Dossier octroyé", order: 4 },
  { key: "travaux_a_realiser", name: "Travaux à réaliser", order: 5 },
  { key: "dossier_installe", name: "Travaux terminés", order: 6 },
  { key: "prime_cee", name: "Prime CEE encaissée", order: 7 },
  { key: "prime_apa", name: "APA encaissée", order: 8 },
  { key: "prime_mpr", name: "MPR encaissée", order: 9 },
  { key: "sav", name: "SAV", order: 10 }
];

// Étapes 174 pour tri et filtre
const STEPS_174 = [
  { key: "audit_avant_travaux", name: "Audit avant travaux", order: 1 },
  { key: "devis_signe", name: "Devis signé", order: 2 },
  { key: "planification_locataires", name: "Planification locataires", order: 3 },
  { key: "date_demarrage_travaux", name: "Date démarrage travaux", order: 4 },
  { key: "travaux_realises", name: "Travaux réalisés", order: 5 },
  { key: "cofrac", name: "COFRAC", order: 6 },
  { key: "signature_doc_clients", name: "Signature Doc clients", order: 7 },
  { key: "depot", name: "Dépôt", order: 8 },
  { key: "paye", name: "Payé", order: 9 }
];

// Étapes Cumulé pour tri et filtre
const STEPS_CUMULE = [
  { key: "audit_avant_travaux", name: "Audit avant travaux", order: 1 },
  { key: "devis_signe", name: "Devis signé", order: 2 },
  { key: "planification_locataires", name: "Planification locataires", order: 3 },
  { key: "date_demarrage_travaux", name: "Date démarrage travaux", order: 4 },
  { key: "travaux_realises", name: "Travaux réalisés", order: 5 },
  { key: "cofrac", name: "COFRAC", order: 6 },
  { key: "signature_doc_clients", name: "Signature Doc clients", order: 7 },
  { key: "depot", name: "Dépôt", order: 8 },
  { key: "paye", name: "Payé 174", order: 9 },
  { key: "compte_mpr", name: "Compte MPR", order: 10 },
  { key: "dossier_valide_ynergie", name: "Dossier validé Ynergie", order: 11 },
  { key: "dossier_octroye", name: "Dossier octroyé", order: 12 },
  { key: "dossier_a_installer", name: "Travaux à réaliser", order: 13 },
  { key: "dossier_installe", name: "Travaux terminés", order: 14 },
  { key: "prime_cee", name: "Prime CEE", order: 15 },
  { key: "prime_apa", name: "APA", order: 16 },
  { key: "prime_mpr", name: "MPR", order: 17 },
  { key: "sav", name: "SAV", order: 18 }
];

// Get next step key for filtering
const getNextStepKey = (steps) => {
  if (!steps || steps.length === 0) return null;
  const nextStep = steps.find(s => s.status !== 'done');
  return nextStep ? nextStep.key : "termine";
};

// Get non-paid items (CEE, APA, MPR that are not "done")
const getNonPaidItems = (steps) => {
  if (!steps) return [];
  const paymentSteps = [
    { key: 'prime_cee', label: 'CEE' },
    { key: 'prime_apa', label: 'APA' },
    { key: 'prime_mpr', label: 'MPR' }
  ];
  
  return paymentSteps
    .filter(ps => {
      const step = steps.find(s => s.key === ps.key);
      return !step || step.status !== 'done';
    })
    .map(ps => ps.label);
};

// Get completion date from dossier_installe step (MPR) or travaux_realises step (174)
const getCompletionDate = (steps, is174) => {
  if (!steps) return null;
  const stepKey = is174 ? 'travaux_realises' : 'dossier_installe';
  const step = steps.find(s => s.key === stepKey);
  return step?.completion_date || null;
};

// Calculate steps progress
const getStepsProgress = (steps) => {
  if (!steps || steps.length === 0) return 0;
  const nonSav = steps.filter(s => !s.key?.toLowerCase().includes("sav"));
  if (nonSav.length === 0) return 0;
  const doneCount = nonSav.filter(s => s.status === 'done').length;
  return Math.round((doneCount / nonSav.length) * 100);
};

// Calculate total surface for 174 dossiers
const getTotalSurface = (surfaces) => {
  if (!surfaces || !Array.isArray(surfaces)) return 0;
  return surfaces.reduce((sum, s) => sum + (s || 0), 0);
};

export default function DossiersList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [profileFilter, setProfileFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [statutFilterMpr, setStatutFilterMpr] = useState("all");
  const [statutFilter174, setStatutFilter174] = useState("all");
  const [statutFilterCumule, setStatutFilterCumule] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkAssignUser, setBulkAssignUser] = useState("");
  const [savingBulk, setSavingBulk] = useState(false);
  const isRestricted = isRestrictedUser();
  const currentRole = getUserRole();
  const isAdmin = currentRole === "admin_full";
  const isAdminLike = currentRole === "admin_full" || currentRole === "admin_restricted" || currentRole === "administratif";
  const isAdministratif = currentRole === "administratif";
  const currentUser = localStorage.getItem("label_energie_user") || "";
  
  // Études Régie state - now dynamic per régie user
  const [regieUsers, setRegieUsers] = useState([]);
  const [teleproUsers, setTeleproUsers] = useState([]);
  const [etudesRegieMap, setEtudesRegieMap] = useState({}); // { username: [etudes] }
  const [etudesFilter, setEtudesFilter] = useState("dossier"); // all, etude, dossier - default to dossier
  const [etudesSearch, setEtudesSearch] = useState("");
  const [etudesProgFilter, setEtudesProgFilter] = useState("all"); // all, 174, mpr, cumule
  
  // P2-8: Attribution filter and users list
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [notifByDossier, setNotifByDossier] = useState({});
  const [usersList, setUsersList] = useState([]);
  
  // Get initial tab from URL or default to "mpr"
  const urlParams = new URLSearchParams(location.search);
  const initialTab = urlParams.get('tab') || 'mpr';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Sort state for each tab
  const [sortMpr, setSortMpr] = useState({ field: "created_at", dir: "desc" });
  const [sort174, setSort174] = useState({ field: "created_at", dir: "desc" });
  const [sortCumule, setSortCumule] = useState({ field: "created_at", dir: "desc" });
  
  const handleSortMpr = (field) => setSortMpr(p => p.field === field ? { ...p, dir: p.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });
  const handleSort174 = (field) => setSort174(p => p.field === field ? { ...p, dir: p.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });
  const handleSortCumule = (field) => setSortCumule(p => p.field === field ? { ...p, dir: p.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchDossiers();
    // Fetch users list for attribution filter
    axios.get(`${API}/users`).then(r => setUsersList(r.data)).catch(() => {});
    // Fetch unread notification counts by dossier
    if (isAdminLike) {
      axios.get(`${API}/notifications/by-dossier`).then(r => setNotifByDossier(r.data || {})).catch(() => {});
    }
    if (isAdminLike) {
      // Fetch all régie users and their études + dossiers
      fetch(`${API}/users-regie`).then(r => r.json()).then(users => {
        setRegieUsers(users);
        users.forEach(u => {
          fetch(`${API}/etudes?source=${encodeURIComponent(u.username)}`)
            .then(r => r.json())
            .then(etudes => setEtudesRegieMap(prev => ({ ...prev, [u.username]: etudes })))
            .catch(() => {});
        });
      }).catch(() => {});
      // Fetch all télépro users and their études + dossiers
      fetch(`${API}/users-telepro`).then(r => r.json()).then(users => {
        setTeleproUsers(users);
        users.forEach(u => {
          fetch(`${API}/etudes?source=${encodeURIComponent(u.username)}`)
            .then(r => r.json())
            .then(etudes => setEtudesRegieMap(prev => ({ ...prev, [u.username]: etudes })))
            .catch(() => {});
        });
      }).catch(() => {});
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Déclencher la recherche côté serveur avec un délai (debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchDossiers(search);
    }, 300); // 300ms de délai pour éviter trop de requêtes
    
    return () => clearTimeout(timeoutId);
  }, [search]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/dossiers?tab=${tab}`, { replace: true });
  };

  const fetchDossiers = async (searchTerm = "") => {
    try {
      // Utiliser le paramètre search côté serveur pour inclure la recherche APF
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const response = await axios.get(`${API}/dossiers${params}`);
      setDossiers(response.data);
    } catch (error) {
      console.error("Error fetching dossiers:", error);
      toast.error("Erreur lors du chargement des dossiers");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignUser || selectedDossiers.length === 0) return;
    setSavingBulk(true);
    try {
      for (const dosId of selectedDossiers) {
        await axios.put(`${API}/dossiers/${dosId}`, { assigned_to: bulkAssignUser });
      }
      toast.success(`${selectedDossiers.length} dossier(s) attribué(s) à ${bulkAssignUser}`);
      setSelectedDossiers([]);
      setShowBulkAssign(false);
      fetchAllData();
    } catch (err) { toast.error("Erreur"); }
    setSavingBulk(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/dossiers/${deleteId}`);
      toast.success("Dossier supprimé avec succès");
      fetchDossiers();
    } catch (error) {
      console.error("Error deleting dossier:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  // Scope dossiers for administratif users
  const [assignedRegies, setAssignedRegies] = useState([]);
  useEffect(() => {
    if (isAdministratif) {
      // Fetch the current user's assigned_regies
      fetch(`${API}/users/${encodeURIComponent(currentUser)}`).then(r => r.json()).then(u => {
        setAssignedRegies(u.assigned_regies || []);
      }).catch(() => {});
    }
  }, [isAdministratif, currentUser]);

  const scopedDossiers = isAdministratif
    ? dossiers.filter(d => {
        // Own dossiers
        if (d.assigned_to === currentUser) return true;
        if (d.client_name === currentUser) return true;
        // Dossiers from assigned régies
        if (assignedRegies.includes(d.assigned_to)) return true;
        if (assignedRegies.includes(d.client_name)) return true;
        return false;
      })
    : dossiers;

  // Collect régie and télépro usernames for filtering
  const regieUsernames = regieUsers.map(u => u.username);
  const teleproUsernames = teleproUsers.map(u => u.username);
  const allSubUsernames = [...regieUsernames, ...teleproUsernames];

  // For admin main tabs: exclude dossiers already assigned to a régie/télépro (they appear in their tab)
  const adminMainDossiers = isAdminLike
    ? scopedDossiers.filter(d => !allSubUsernames.includes(d.assigned_to))
    : scopedDossiers;

  // For régie/télépro tabs: get dossiers from main collection assigned to that user
  const getDossiersForUser = (username) => {
    return dossiers.filter(d => d.assigned_to === username);
  };

  // Filter dossiers by tab
  const mprDossiers = adminMainDossiers.filter(d => d.programme !== "174" && d.programme !== "cumule");
  const dossiers174 = adminMainDossiers.filter(d => d.programme === "174");
  const dossiersCumule = adminMainDossiers.filter(d => d.programme === "cumule");

  // Apply search and filters
  const filterDossiers = (dossiersToFilter, statutFilter, stepsList) => {
    return dossiersToFilter.filter((dossier) => {
      const matchesProfile = profileFilter === "all" || dossier.profile === profileFilter;
      const matchesStatus = statusFilter === "all" || dossier.status === statusFilter;
      
      // Filtre Statut (dernière étape validée)
      let matchesStatut = true;
      if (statutFilter !== "all") {
        const lastDoneKey = getLastDoneStepKey(dossier.steps);
        matchesStatut = lastDoneKey === statutFilter;
      }
      
      // P2-8: Filtre attribution
      let matchesAssigned = true;
      if (assignedFilter !== "all") {
        if (assignedFilter === "non_attribue") {
          matchesAssigned = !dossier.assigned_to || dossier.assigned_to === "non_attribue";
        } else {
          matchesAssigned = dossier.assigned_to === assignedFilter;
        }
      }
      
      return matchesProfile && matchesStatus && matchesStatut && matchesAssigned;
    });
  };

  // Generic sort function for dossier lists
  const sortDossiers = (list, sortState, is174 = false) => {
    return [...list].sort((a, b) => {
      let va, vb;
      const { field, dir } = sortState;
      switch (field) {
        case "created_at": va = a.created_at || ""; vb = b.created_at || ""; break;
        case "statut_dossier": va = getLastDoneStepName(a.steps); vb = getLastDoneStepName(b.steps); break;
        case "eta": va = getNextStepName(a.steps); vb = getNextStepName(b.steps); break;
        case "client_name": va = (a.client_name || "").toLowerCase(); vb = (b.client_name || "").toLowerCase(); break;
        case "zone": va = a.zone || ""; vb = b.zone || ""; break;
        case "metrage": va = parseFloat(a.metrage) || 0; vb = parseFloat(b.metrage) || 0; break;
        case "precarite": va = a.precarite || ""; vb = b.precarite || ""; break;
        case "steps_progress": va = getStepsProgress(a.steps); vb = getStepsProgress(b.steps); break;
        case "sous_traitant": va = a.sous_traitant || ""; vb = b.sous_traitant || ""; break;
        case "completion_date": va = getCompletionDate(a.steps, is174) || ""; vb = getCompletionDate(b.steps, is174) || ""; break;
        case "total_payments": va = a.total_payments || 0; vb = b.total_payments || 0; break;
        case "non_paid": va = getNonPaidItems(a.steps).length; vb = getNonPaidItems(b.steps).length; break;
        case "marge_prevue": va = a.marge_prevue || 0; vb = b.marge_prevue || 0; break;
        case "assigned_to": va = a.assigned_to || ""; vb = b.assigned_to || ""; break;
        case "nombre_logements": va = a.nombre_logements || 0; vb = b.nombre_logements || 0; break;
        case "montant_devis_174": va = a.montant_devis_174 || 0; vb = b.montant_devis_174 || 0; break;
        default: va = a.created_at || ""; vb = b.created_at || "";
      }
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Tri par progression (ordre des étapes)
  const filteredMprDossiers = sortDossiers(filterDossiers(mprDossiers, statutFilterMpr, STEPS_MPR), sortMpr, false);

  const filtered174Dossiers = sortDossiers(filterDossiers(dossiers174, statutFilter174, STEPS_174), sort174, true);

  const filteredCumuleDossiers = sortDossiers(filterDossiers(dossiersCumule, statutFilterCumule, STEPS_CUMULE), sortCumule, false);

  // Fonction d'export de la liste des dossiers
  const handleExportList = async (format) => {
    try {
      toast.info(`Export ${format.toUpperCase()} en cours...`);
      
      const currentDossiers = activeTab === 'mpr' ? filteredMprDossiers : activeTab === '174' ? filtered174Dossiers : filteredCumuleDossiers;
      
      const response = await axios.post(`${API}/dossiers/export-list`, {
        dossier_ids: currentDossiers.map(d => d.id),
        format: format,
        programme: activeTab === 'mpr' ? 'MPR' : activeTab === '174' ? '174' : 'Cumulé'
      }, {
        responseType: 'blob'
      });
      
      // Télécharger le fichier
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dossiers_${activeTab}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}`);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-muted rounded-sm"></div>
          ))}
        </div>
      </div>
    );
  }

  // MPR Table Component
  const MprTable = ({ data }) => (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <table className="w-full table-fixed">
        <thead>
          <tr className="bg-muted/50">
            <th className="w-[30px] px-1 py-2.5 border-b border-border"></th>
            <SortHeader label="Date" field="created_at" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[70px]" />
            <SortHeader label="Statut" field="statut_dossier" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[75px]" />
            <SortHeader label="ETA" field="eta" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[75px]" />
            <SortHeader label="Client" field="client_name" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[100px]" />
            <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Tél.</th>
            <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Prog.</th>
            <SortHeader label="Zone" field="zone" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[40px]" />
            <SortHeader label="m²" field="metrage" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[40px]" />
            <SortHeader label="Préc." field="precarite" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[40px]" />
            <SortHeader label="Étapes" field="steps_progress" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[60px]" />
            <SortHeader label="Poseur" field="sous_traitant" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[60px]" />
            <SortHeader label="Travaux" field="completion_date" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[65px]" />
            <SortHeader label="Encaissé" field="total_payments" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[65px]" />
            <SortHeader label="Non enc." field="non_paid" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[75px]" />
            {!isRestricted && (
            <SortHeader label="Marge" field="marge_prevue" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[60px]" />
            )}
            <SortHeader label="Attribué" field="assigned_to" sortField={sortMpr.field} sortDir={sortMpr.dir} onSort={handleSortMpr} className="w-[65px]" />
            <th className="px-2 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[35px]">
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.map((dossier) => {
            const nonPaid = getNonPaidItems(dossier.steps);
            const completionDate = getCompletionDate(dossier.steps, false);
            const stepsProgress = getStepsProgress(dossier.steps);
            const precariteConfig = PRECARITE_CONFIG[dossier.precarite];
            const nextStep = getNextStepName(dossier.steps);
            const lastDoneStep = getLastDoneStepName(dossier.steps);
            // Check if step 9 (SAV) is done
            const lastStep = dossier.steps?.[dossier.steps?.length - 1];
            const isCompleted = lastStep?.status === 'done';
            
            return (
              <tr 
                key={dossier.id}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/dossiers/${dossier.id}`)}
                data-testid={`dossier-row-${dossier.id}`}
              >
                <td className="px-1 py-2" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedDossiers.includes(dossier.id)} onChange={() => setSelectedDossiers(prev => prev.includes(dossier.id) ? prev.filter(x => x !== dossier.id) : [...prev, dossier.id])} className="w-3.5 h-3.5 rounded" />
                </td>
                <td className="px-2 py-2 text-[10px] text-muted-foreground">
                  {dossier.created_at ? new Date(dossier.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "-"}
                </td>
                <td className="px-2 py-2">
                  <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                    dossier.statut_dossier === "nouveau" ? "bg-slate-100 text-slate-600" :
                    dossier.statut_dossier === "termine" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" :
                    "bg-blue-500/20 text-blue-600 dark:text-blue-400"
                  )} data-testid={`statut-${dossier.id}`}>
                    {(dossier.statut_dossier || "nouveau").replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).substring(0, 14)}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <span className="text-[10px] text-muted-foreground truncate block max-w-[70px]" title={lastDoneStep}>
                    {lastDoneStep.length > 12 ? lastDoneStep.substring(0, 12) + '...' : lastDoneStep}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <p className="font-medium text-sm truncate max-w-[100px]">{dossier.client_name}</p>
                </td>
                <td className="px-2 py-2 text-[10px] text-muted-foreground truncate">{dossier.client_phone || "-"}</td>
                <td className="px-2 py-2">
                  <span className="text-[10px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-700 font-semibold">{dossier.programme || "-"}</span>
                </td>
                <td className="px-2 py-2">
                  {dossier.zone ? (
                    <span className="text-xs font-semibold text-primary">{dossier.zone}</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs tabular-nums">
                  {dossier.metrage || <span className="text-muted-foreground">-</span>}
                </td>
                <td className="px-2 py-2">
                  {precariteConfig ? (
                    <div className="flex items-center gap-1">
                      <div className={cn("w-2.5 h-2.5 rounded-full", precariteConfig.color)}></div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    <Progress value={stepsProgress} className="w-10 h-1.5" />
                    <span className="text-[10px] font-medium tabular-nums">{stepsProgress}%</span>
                  </div>
                </td>
                <td className="px-2 py-2">
                  {dossier.sous_traitant ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/10 text-amber-700 border border-amber-500/30 rounded-sm">
                      {dossier.sous_traitant}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {completionDate ? (
                    <span className="text-[hsl(145,100%,39%)] font-medium">{formatDate(completionDate)}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  <span className={cn(
                    "font-semibold tabular-nums text-xs",
                    dossier.total_payments > 0 ? "text-[hsl(145,100%,39%)]" : "text-muted-foreground"
                  )}>
                    {formatCurrency(dossier.total_payments || 0)}
                  </span>
                </td>
                <td className="px-2 py-2">
                  {nonPaid.length > 0 ? (
                    <div className="flex items-center gap-0.5">
                      {nonPaid.map(item => (
                        <span 
                          key={item}
                          className="inline-flex items-center px-1 py-0 text-[9px] font-medium bg-red-500/10 text-red-600 border border-red-500/30 rounded-sm"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[10px] text-[hsl(145,100%,39%)]">✓</span>
                  )}
                </td>
                {!isRestricted && (
                <td className="px-2 py-2">
                  <span className={cn(
                    "font-semibold tabular-nums text-xs",
                    (dossier.marge_prevue || 0) >= 0 ? "text-[hsl(145,100%,39%)]" : "text-red-600"
                  )}>
                    {formatCurrency(dossier.marge_prevue || 0)}
                  </span>
                </td>
                )}
                <td className="px-2 py-2">
                  {dossier.assigned_to && dossier.assigned_to !== "non_attribue" ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-indigo-500/10 text-indigo-700 border border-indigo-200 rounded-sm" data-testid={`assigned-${dossier.id}`}>
                      {dossier.assigned_to}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-[10px]">-</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(dossier.id);
                    }}
                    className="rounded-xl text-muted-foreground hover:text-destructive h-7 w-7 p-0"
                    data-testid={`delete-dossier-${dossier.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // 174 Table Component
  const Table174 = ({ data, sortState = sort174, onSortChange = handleSort174 }) => (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <table className="w-full table-fixed">
        <thead>
          <tr className="bg-muted/50">
            <th className="w-[30px] px-1 py-2.5 border-b border-border"></th>
            <SortHeader label="Date" field="created_at" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[60px]" />
            <SortHeader label="Statut" field="statut_dossier" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[75px]" />
            <SortHeader label="ETA" field="eta" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[75px]" />
            <SortHeader label="Client" field="client_name" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[100px]" />
            <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Tél.</th>
            <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[100px]">
              Bénéficiaire
            </th>
            <SortHeader label="Zone" field="zone" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[35px]" />
            <SortHeader label="m² Tot." field="metrage" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[45px]" />
            <SortHeader label="Progr." field="steps_progress" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[55px]" />
            <SortHeader label="Poseur" field="sous_traitant" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[55px]" />
            <SortHeader label="Travaux" field="completion_date" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[60px]" />
            <SortHeader label="Encaissé" field="total_payments" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[60px]" />
            {!isRestricted && (
            <SortHeader label="Marge" field="marge_prevue" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[55px]" />
            )}
            <SortHeader label="Attribué" field="assigned_to" sortField={sortState.field} sortDir={sortState.dir} onSort={onSortChange} className="w-[65px]" />
            <th className="px-2 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[30px]">
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.map((dossier) => {
            const completionDate = getCompletionDate(dossier.steps, true);
            const stepsProgress = getStepsProgress(dossier.steps);
            const totalSurface = getTotalSurface(dossier.surfaces_logements);
            const nextStep = getNextStepName(dossier.steps);
            const lastDoneStep = getLastDoneStepName(dossier.steps);
            // Get payment from "paye" step
            const payeStep = dossier.steps?.find(s => s.key === 'paye');
            const encaisse = payeStep?.amount || 0;
            // Check if step 9 (Payé) is done
            const lastStep = dossier.steps?.[dossier.steps?.length - 1];
            const isCompleted = lastStep?.status === 'done';
            
            return (
              <tr 
                key={dossier.id}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/dossiers/${dossier.id}`)}
                data-testid={`dossier-row-${dossier.id}`}
              >
                <td className="px-1 py-2" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedDossiers.includes(dossier.id)} onChange={() => setSelectedDossiers(prev => prev.includes(dossier.id) ? prev.filter(x => x !== dossier.id) : [...prev, dossier.id])} className="w-3.5 h-3.5 rounded" />
                </td>
                <td className="px-2 py-2 text-[10px] text-muted-foreground">
                  {dossier.created_at ? new Date(dossier.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "-"}
                </td>
                <td className="px-2 py-2">
                  <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                    dossier.statut_dossier === "nouveau" ? "bg-slate-100 text-slate-600" :
                    dossier.statut_dossier === "termine" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" :
                    "bg-blue-500/20 text-blue-600 dark:text-blue-400"
                  )} data-testid={`statut-174-${dossier.id}`}>
                    {(dossier.statut_dossier || "nouveau").replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).substring(0, 14)}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <span className="text-[10px] text-muted-foreground truncate block max-w-[70px]" title={lastDoneStep}>
                    {lastDoneStep.length > 12 ? lastDoneStep.substring(0, 12) + '...' : lastDoneStep}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <p className="font-medium text-sm truncate max-w-[90px]">{dossier.client_name}</p>
                </td>
                <td className="px-2 py-2 text-[10px] text-muted-foreground truncate">{dossier.client_phone || "-"}</td>
                <td className="px-2 py-2">
                  <p className="text-xs truncate max-w-[90px] text-muted-foreground">
                    {dossier.nom_residence || "-"}
                  </p>
                </td>
                <td className="px-2 py-2">
                  {dossier.zone ? (
                    <span className="text-xs font-semibold text-primary">{dossier.zone}</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs tabular-nums">
                  {totalSurface > 0 ? totalSurface : <span className="text-muted-foreground">-</span>}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    <Progress value={stepsProgress} className="w-10 h-1.5" />
                    <span className="text-[10px] font-medium tabular-nums">{stepsProgress}%</span>
                  </div>
                </td>
                <td className="px-2 py-2">
                  {dossier.sous_traitant ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/10 text-amber-700 border border-amber-500/30 rounded-sm dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                      {dossier.sous_traitant}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {completionDate ? (
                    <span className="text-[hsl(145,100%,39%)] font-medium">{formatDate(completionDate)}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  <span className={cn(
                    "font-semibold tabular-nums text-xs",
                    encaisse > 0 ? "text-[hsl(145,100%,39%)]" : "text-muted-foreground"
                  )}>
                    {formatCurrency(encaisse)}
                  </span>
                </td>
                {!isRestricted && (
                <td className="px-2 py-2">
                  <span className={cn(
                    "font-semibold tabular-nums text-xs",
                    (dossier.marge_prevue || 0) >= 0 ? "text-[hsl(145,100%,39%)]" : "text-red-600"
                  )}>
                    {formatCurrency(dossier.marge_prevue || 0)}
                  </span>
                </td>
                )}
                <td className="px-2 py-2">
                  {dossier.assigned_to && dossier.assigned_to !== "non_attribue" ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-indigo-500/10 text-indigo-700 border border-indigo-200 rounded-sm" data-testid={`assigned-174-${dossier.id}`}>
                      {dossier.assigned_to}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-[10px]">-</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(dossier.id);
                    }}
                    className="rounded-xl text-muted-foreground hover:text-destructive h-7 w-7 p-0"
                    data-testid={`delete-dossier-${dossier.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Empty state component
  const EmptyState = ({ type }) => (
    <div className="card-floating p-12 text-center">
      <FolderOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
      <h3 className="font-heading font-semibold text-lg mb-2">
        Aucun dossier {type === "174" ? "174" : type === "cumule" ? "Opération Cumulée" : "MPR"}
      </h3>
      <p className="text-muted-foreground mb-6">
        {type === "cumule" 
          ? "Utilisez le Simulateur par profil pour créer un dossier combiné 174 + MPR"
          : `Commencez par créer votre premier dossier ${type === "174" ? "de rénovation globale" : "MPR"}`}
      </p>
      {type === "cumule" ? (
        <Button 
          onClick={() => navigate('/simulateur-travaux')}
          className="rounded-xl bg-violet-600 hover:bg-violet-700"
          data-testid="empty-create-cumule-btn"
        >
          Simulateur par profil
        </Button>
      ) : (
        <Button 
          onClick={() => navigate(`/simulateur-travaux`)}
          className="rounded-xl bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]"
          data-testid="empty-create-btn"
        >
          Créer un dossier
        </Button>
      )}
    </div>
  );

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
            Dossiers
          </h1>
          <p className="text-muted-foreground mt-1">
            {dossiers.length} dossier{dossiers.length > 1 ? 's' : ''} au total
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export buttons — Renova only */}
          {!isRestricted && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline"
                className="rounded-xl"
                data-testid="export-list-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => handleExportList('excel')}
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Export Excel
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExportList('pdf')}
                data-testid="export-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Export PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          
          <Button 
            data-testid="new-dossier-btn"
            onClick={() => navigate(`/simulateur-travaux`)}
            className="rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouveau dossier
          </Button>
          {selectedDossiers.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{selectedDossiers.length} sélectionné(s)</span>
              <Button onClick={() => setShowBulkAssign(true)} size="sm" className="rounded-xl bg-violet-500 hover:bg-violet-600 text-white">
                Attribuer
              </Button>
              <Button onClick={async () => {
                if (!confirm(`Supprimer ${selectedDossiers.length} dossier(s) ?`)) return;
                for (const dosId of selectedDossiers) {
                  await axios.delete(`${API}/dossiers/${dosId}`).catch(() => axios.delete(`${API}/etudes/${dosId}?role=admin_full`));
                }
                toast.success(`${selectedDossiers.length} dossier(s) supprimé(s)`);
                setSelectedDossiers([]);
                fetchAllData();
              }} size="sm" variant="outline" className="rounded-xl text-red-600 border-red-300 hover:bg-red-500/10">
                Supprimer
              </Button>
              <Button onClick={() => setSelectedDossiers([])} size="sm" variant="ghost" className="rounded-xl text-muted-foreground">
                Désélectionner
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs MPR / 174 */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <TabsList className="bg-card rounded-xl p-1.5 shadow-lg border border-border">
            <TabsTrigger 
              value="mpr" 
              className="rounded-xl data-[state=active]:bg-[hsl(145,100%,45%)]/10 data-[state=active]:text-[hsl(145,100%,39%)] px-6 py-2.5 font-semibold transition-all"
              data-testid="tab-mpr"
            >
              MPR ({mprDossiers.length})
            </TabsTrigger>
            <TabsTrigger 
              value="174" 
              className="rounded-xl data-[state=active]:bg-[hsl(145,100%,45%)]/10 data-[state=active]:text-[hsl(145,100%,39%)] px-6 py-2.5 font-semibold transition-all"
              data-testid="tab-174"
            >
              174 ({dossiers174.length})
            </TabsTrigger>
            <TabsTrigger 
              value="cumule" 
              className="rounded-xl data-[state=active]:bg-violet-500/10 data-[state=active]:text-violet-700 px-6 py-2.5 font-semibold transition-all"
              data-testid="tab-cumule"
            >
              Op. Cumulée ({dossiersCumule.length})
            </TabsTrigger>
            {isAdminLike && regieUsers.filter(u => u.active !== false).map(ru => (
              <TabsTrigger 
                key={ru.username}
                value={`regie-${ru.username}`}
                className="rounded-xl data-[state=active]:bg-orange-500/10 data-[state=active]:text-orange-700 px-6 py-2.5 font-semibold transition-all"
                data-testid={`tab-regie-${ru.username}`}
              >
                {ru.username} ({(etudesRegieMap[ru.username] || []).filter(e => e.type === "dossier").length + getDossiersForUser(ru.username).length})
              </TabsTrigger>
            ))}
            {isAdminLike && teleproUsers.map(tu => (
              <TabsTrigger 
                key={tu.username}
                value={`telepro-${tu.username}`}
                className="rounded-xl data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-700 px-6 py-2.5 font-semibold transition-all"
                data-testid={`tab-telepro-${tu.username}`}
              >
                {tu.username} ({(etudesRegieMap[tu.username] || []).filter(e => e.type === "dossier").length + getDossiersForUser(tu.username).length})
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-input"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-xl w-[200px] bg-card border-border"
              />
            </div>
            {/* Filtre Statut - conditionnel par onglet */}
            {activeTab === 'mpr' ? (
              <Select value={statutFilterMpr} onValueChange={setStatutFilterMpr}>
                <SelectTrigger className="w-[180px] rounded-xl bg-card" data-testid="statut-filter-mpr">
                  <Clock className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="non_demarre">Non démarré</SelectItem>
                  {STEPS_MPR.map(step => (
                    <SelectItem key={step.key} value={step.key}>{step.name}</SelectItem>
                  ))}
                  <SelectItem value="termine">Terminé</SelectItem>
                </SelectContent>
              </Select>
            ) : activeTab === '174' ? (
              <Select value={statutFilter174} onValueChange={setStatutFilter174}>
                <SelectTrigger className="w-[180px] rounded-xl bg-card" data-testid="statut-filter-174">
                  <Clock className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="non_demarre">Non démarré</SelectItem>
                  {STEPS_174.map(step => (
                    <SelectItem key={step.key} value={step.key}>{step.name}</SelectItem>
                  ))}
                  <SelectItem value="termine">Terminé</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Select value={statutFilterCumule} onValueChange={setStatutFilterCumule}>
                <SelectTrigger className="w-[180px] rounded-xl bg-card" data-testid="statut-filter-cumule">
                  <Clock className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="non_demarre">Non démarré</SelectItem>
                  {STEPS_CUMULE.map(step => (
                    <SelectItem key={step.key} value={step.key}>{step.name}</SelectItem>
                  ))}
                  <SelectItem value="termine">Terminé</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={profileFilter} onValueChange={setProfileFilter}>
              <SelectTrigger className="w-[160px] rounded-xl bg-card" data-testid="profile-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Profil" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Tous les profils</SelectItem>
                <SelectItem value="bailleur">Bailleur</SelectItem>
                {activeTab === 'mpr' && <SelectItem value="proprietaire_occupant">Propriétaire</SelectItem>}
                <SelectItem value="sci">SCI</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] rounded-xl bg-card" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="complete">Complet</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="incomplete">Incomplet</SelectItem>
              </SelectContent>
            </Select>
            <Select value={assignedFilter} onValueChange={setAssignedFilter}>
              <SelectTrigger className="w-[160px] rounded-xl bg-card" data-testid="assigned-filter">
                <SelectValue placeholder="Attribué à" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="non_attribue">Non attribué</SelectItem>
                {usersList.map(u => (
                  <SelectItem key={u.username} value={u.username}>{u.username}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* MPR Tab Content */}
        <TabsContent value="mpr" className="mt-6">
          {filteredMprDossiers.length > 0 ? (
            <div className="card-floating overflow-hidden">
              <MprTable data={filteredMprDossiers} />
            </div>
          ) : (
            <EmptyState type="mpr" />
          )}
        </TabsContent>

        {/* 174 Tab Content */}
        <TabsContent value="174" className="mt-6">
          {filtered174Dossiers.length > 0 ? (
            <div className="card-floating overflow-hidden">
              <Table174 data={filtered174Dossiers} />
            </div>
          ) : (
            <EmptyState type="174" />
          )}
        </TabsContent>

        {/* Cumulé Tab Content */}
        <TabsContent value="cumule" className="mt-6">
          {filteredCumuleDossiers.length > 0 ? (
            <div className="card-floating overflow-hidden">
              <Table174 data={filteredCumuleDossiers} sortState={sortCumule} onSortChange={handleSortCumule} />
            </div>
          ) : (
            <EmptyState type="cumule" />
          )}
        </TabsContent>

        {/* Dynamic Régie Tabs Content */}
        {isAdminLike && regieUsers.filter(u => u.active !== false).map(ru => (
          <TabsContent key={ru.username} value={`regie-${ru.username}`} className="mt-6 space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input value={etudesSearch} onChange={e => setEtudesSearch(e.target.value)} placeholder="Rechercher..." className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-sm" data-testid={`search-regie-${ru.username}`} />
              </div>
              <select value={etudesFilter} onChange={e => setEtudesFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-border bg-background text-sm" data-testid={`filter-type-${ru.username}`}>
                <option value="dossier">Dossiers</option>
                <option value="etude">Études</option>
                <option value="all">Tous</option>
              </select>
              <select value={etudesProgFilter} onChange={e => setEtudesProgFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-border bg-background text-sm" data-testid={`filter-prog-${ru.username}`}>
                <option value="all">Tous les programmes</option>
                <option value="174">174</option>
                <option value="mpr">MPR</option>
                <option value="cumule">Cumulée</option>
                <option value="sci">Dossier SCI</option>
              </select>
            </div>

            {(() => {
              const etudesData = etudesRegieMap[ru.username] || [];
              const assignedDossiers = getDossiersForUser(ru.username).map(d => ({
                id: d.id,
                type: "dossier",
                created_at: d.created_at,
                beneficiaire: { nom: d.client_name, telephone: d.client_phone },
                profil: d.profile,
                surface: d.metrage,
                zone: d.zone,
                precarite: d.precarite,
                recommendation: d.programme,
                status: d.statut_dossier,
                source: "admin_assigned",
                is_sci_multi: d.is_sci_multi || false,
                marge_prevue: d.marge_prevue || 0,
                total_payments: d.total_payments || 0,
                quote_amount: d.quote_amount || 0,
                steps: d.steps || [],
              }));
              // Enrich etudes with matching dossier data
              const dossiersByName = {};
              getDossiersForUser(ru.username).forEach(d => {
                const name = (d.client_name || "").toLowerCase();
                if (name) dossiersByName[name] = d;
              });
              const enrichedEtudes = etudesData.map(e => {
                const name = (e.beneficiaire?.nom || "").toLowerCase();
                const match = dossiersByName[name];
                if (match) {
                  return {
                    ...e,
                    marge_prevue: match.marge_prevue || e.marge_prevue || 0,
                    total_payments: match.total_payments || e.total_payments || 0,
                    quote_amount: match.quote_amount || e.quote_amount || 0,
                    steps: match.steps?.length > 0 ? match.steps : e.steps || [],
                    precarite: match.precarite || e.precarite,
                    zone: match.zone || e.zone,
                  };
                }
                return e;
              });
              // Merge: enriched études + assigned dossiers (avoid duplicates)
              const existingIds = new Set(enrichedEtudes.map(e => e.id));
              const allItems = [...enrichedEtudes, ...assignedDossiers.filter(d => !existingIds.has(d.id))];
              const filteredItems = allItems.filter(e => {
                if (etudesFilter === "dossier" && e.type !== "dossier") return false;
                if (etudesFilter === "etude" && e.type === "dossier") return false;
                if (etudesProgFilter === "sci" && !e.is_sci_multi) return false;
                if (etudesProgFilter !== "all" && etudesProgFilter !== "sci" && e.recommendation !== etudesProgFilter) return false;
                if (etudesSearch) {
                  const s = etudesSearch.toLowerCase();
                  const name = (e.beneficiaire?.nom || "").toLowerCase();
                  const eid = (e.id || "").toLowerCase();
                  if (!name.includes(s) && !eid.includes(s)) return false;
                }
                return true;
              });
              return filteredItems.length > 0 ? (
              <div className="card-floating overflow-hidden">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="w-[30px] px-1 py-2.5 border-b border-border"></th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[70px]">Date</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[75px]">Statut</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[75px]">ETA</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[100px]">Client</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Tél.</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Prog.</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[40px]">Zone</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[40px]">m²</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[40px]">Préc.</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[60px]">Étapes</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[65px]">Encaissé</th>
                      {!isRestricted && <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[60px]">Marge</th>}
                      <th className="px-2 py-2.5 border-b border-border w-[30px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(e => {
                      const isDossier = e.type === "dossier";
                      const clientName = e.beneficiaire?.nom || "";
                      const clientPhone = e.beneficiaire?.telephone || "";
                      const prog = e.recommendation || e.programme || "-";
                      const zone = e.zone || "-";
                      const surface = e.surface || e.metrage || 0;
                      const precarite = e.precarite || "-";
                      const stepsArr = e.steps || [];
                      const stepsProgress = stepsArr.length > 0 ? Math.round(stepsArr.filter(s => s.status === "done").length / stepsArr.length * 100) : 0;
                      const totalPay = e.total_payments || 0;
                      const marge = e.marge_prevue || 0;
                      const eta = isDossier ? (e.status || e.statut_dossier || "-") : "Étude";
                      const createdDate = e.created_at ? new Date(e.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
                      return (
                        <tr key={e.id}
                          className={cn("border-b border-border/40 hover:bg-muted/20 cursor-pointer transition-colors", isDossier && "bg-indigo-500/10")}
                          onClick={() => navigate(isDossier ? `/dossiers/${e.id}` : `/etude/${e.id}`)}
                          data-testid={`etude-row-${e.id}`}>
                          <td className="px-1 py-2" onClick={e2 => e2.stopPropagation()}>
                            <input type="checkbox" checked={selectedDossiers.includes(e.id)} onChange={() => setSelectedDossiers(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id])} className="w-3.5 h-3.5 rounded" />
                          </td>
                          <td className="px-2 py-2 text-[10px] text-muted-foreground">{createdDate}</td>
                          <td className="px-2 py-2">
                            <span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold truncate inline-block max-w-[70px]",
                              isDossier ? "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400" : "bg-orange-500/20 text-orange-600 dark:text-orange-400"
                            )}>{isDossier ? "Dossier" : "Étude"}</span>
                          </td>
                          <td className="px-2 py-2 text-[10px]">
                            <span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold",
                              eta === "rdv_admin" ? "bg-blue-500/20 text-blue-600" :
                              eta === "Devis signe" ? "bg-emerald-500/20 text-emerald-600" : "bg-muted/50 text-muted-foreground"
                            )}>{eta === "rdv_admin" ? "RDV" : eta?.substring(0, 12) || "-"}</span>
                          </td>
                          <td className="px-2 py-2 text-xs font-semibold truncate max-w-[100px]">{clientName || "-"}</td>
                          <td className="px-2 py-2 text-[10px] text-muted-foreground truncate">{clientPhone || "-"}</td>
                          <td className="px-2 py-2">
                            <span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold uppercase",
                              prog === "cumule" ? "bg-violet-500/20 text-violet-600" :
                              prog === "174" ? "bg-emerald-500/20 text-emerald-600" : "bg-blue-500/20 text-blue-600"
                            )}>{prog === "cumule" ? "CUM" : prog || "-"}</span>
                          </td>
                          <td className="px-2 py-2 text-[10px]">{zone}</td>
                          <td className="px-2 py-2 text-[10px]">{surface || "-"}</td>
                          <td className="px-2 py-2">
                            <span className={cn("w-4 h-4 rounded-full inline-flex items-center justify-center text-[8px] font-bold",
                              precarite?.toLowerCase() === "bleu" ? "bg-blue-500 text-white" : "bg-yellow-400 text-black"
                            )}>{precarite?.charAt(0)?.toUpperCase() || "?"}</span>
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-1">
                              <Progress value={stepsProgress} className="w-10 h-1.5" />
                              <span className="text-[10px] font-medium">{stepsProgress}%</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-[10px] font-semibold tabular-nums">{totalPay > 0 ? formatCurrency(totalPay) : "-"}</td>
                          {!isRestricted && <td className="px-2 py-2 text-[10px] font-semibold tabular-nums text-emerald-600">{marge ? formatCurrency(marge) : "-"}</td>}
                          <td className="px-1 py-2" onClick={e2 => e2.stopPropagation()}>
                            <button onClick={async () => {
                              if (!confirm(`Supprimer ${clientName || "ce dossier"} ?`)) return;
                              try {
                                await axios.delete(`${API}/dossiers/${e.id}`).catch(() => axios.delete(`${API}/etudes/${e.id}?role=admin_full`))
                                toast.success("Supprimé"); fetchAllData();
                              } catch (err) { toast.error("Erreur"); }
                            }} className="text-red-500 hover:text-red-700 hover:bg-red-500/10 p-1 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-border rounded-xl">
                <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucune étude pour {ru.username}</p>
              </div>
            );
            })()}
          </TabsContent>
        ))}

        {/* Dynamic Télépro Tabs Content */}
        {isAdminLike && teleproUsers.filter(u => u.active !== false).map(ru => (
          <TabsContent key={ru.username} value={`telepro-${ru.username}`} className="mt-6 space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input value={etudesSearch} onChange={e => setEtudesSearch(e.target.value)} placeholder="Rechercher..." className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-sm" />
              </div>
              <select value={etudesFilter} onChange={e => setEtudesFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-border bg-background text-sm">
                <option value="dossier">Dossiers</option>
                <option value="etude">Études</option>
                <option value="all">Tous</option>
              </select>
              <select value={etudesProgFilter} onChange={e => setEtudesProgFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-border bg-background text-sm">
                <option value="all">Tous les programmes</option>
                <option value="174">174</option>
                <option value="mpr">MPR</option>
                <option value="cumule">Cumulée</option>
                <option value="sci">Dossier SCI</option>
              </select>
            </div>
            {(() => {
              const etudesData = etudesRegieMap[ru.username] || [];
              const assignedDossiers = getDossiersForUser(ru.username).map(d => ({
                id: d.id,
                type: "dossier",
                created_at: d.created_at,
                beneficiaire: { nom: d.client_name, telephone: d.client_phone },
                profil: d.profile,
                surface: d.metrage,
                zone: d.zone,
                precarite: d.precarite,
                recommendation: d.programme,
                status: d.statut_dossier,
                source: "admin_assigned",
                is_sci_multi: d.is_sci_multi || false,
                marge_prevue: d.marge_prevue || 0,
                total_payments: d.total_payments || 0,
                quote_amount: d.quote_amount || 0,
                steps: d.steps || [],
              }));
              const dossiersByName2 = {};
              getDossiersForUser(ru.username).forEach(d => {
                const name = (d.client_name || "").toLowerCase();
                if (name) dossiersByName2[name] = d;
              });
              const enrichedEtudes2 = etudesData.map(e => {
                const name = (e.beneficiaire?.nom || "").toLowerCase();
                const match = dossiersByName2[name];
                if (match) {
                  return { ...e, marge_prevue: match.marge_prevue || 0, total_payments: match.total_payments || 0, quote_amount: match.quote_amount || 0, steps: match.steps?.length > 0 ? match.steps : e.steps || [], precarite: match.precarite || e.precarite, zone: match.zone || e.zone };
                }
                return e;
              });
              const existingIds = new Set(enrichedEtudes2.map(e => e.id));
              const allItems = [...enrichedEtudes2, ...assignedDossiers.filter(d => !existingIds.has(d.id))];
              const filteredItems = allItems.filter(e => {
                if (etudesFilter === "dossier" && e.type !== "dossier") return false;
                if (etudesFilter === "etude" && e.type === "dossier") return false;
                if (etudesProgFilter === "sci" && !e.is_sci_multi) return false;
                if (etudesProgFilter !== "all" && etudesProgFilter !== "sci" && e.recommendation !== etudesProgFilter) return false;
                if (etudesSearch) { const s = etudesSearch.toLowerCase(); const name = (e.beneficiaire?.nom || "").toLowerCase(); const eid = (e.id || "").toLowerCase(); if (!name.includes(s) && !eid.includes(s)) return false; }
                return true;
              });
              return filteredItems.length > 0 ? (
              <div className="card-floating overflow-hidden">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="w-[30px] px-1 py-2.5 border-b border-border"></th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[70px]">Date</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[75px]">Statut</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[75px]">ETA</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[100px]">Client</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Tél.</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[55px]">Prog.</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[40px]">Zone</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[40px]">m²</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[40px]">Préc.</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[60px]">Étapes</th>
                      <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[65px]">Encaissé</th>
                      {!isRestricted && <th className="px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border w-[60px]">Marge</th>}
                      <th className="px-2 py-2.5 border-b border-border w-[30px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(e => {
                      const isDossier = e.type === "dossier";
                      const clientName = e.beneficiaire?.nom || "";
                      const clientPhone = e.beneficiaire?.telephone || "";
                      const prog = e.recommendation || e.programme || "-";
                      const zone = e.zone || "-";
                      const surface = e.surface || e.metrage || 0;
                      const precarite = e.precarite || "-";
                      const stepsArr = e.steps || [];
                      const stepsProgress = stepsArr.length > 0 ? Math.round(stepsArr.filter(s => s.status === "done").length / stepsArr.length * 100) : 0;
                      const totalPay = e.total_payments || 0;
                      const marge = e.marge_prevue || 0;
                      const eta = isDossier ? (e.status || e.statut_dossier || "-") : "Étude";
                      const createdDate = e.created_at ? new Date(e.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
                      return (
                        <tr key={e.id}
                          className={cn("border-b border-border/40 hover:bg-muted/20 cursor-pointer transition-colors", isDossier && "bg-indigo-500/10")}
                          onClick={() => navigate(isDossier ? `/dossiers/${e.id}` : `/etude/${e.id}`)}>
                          <td className="px-2 py-2 text-[10px] text-muted-foreground">{createdDate}</td>
                          <td className="px-2 py-2"><span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold", isDossier ? "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400" : "bg-orange-500/20 text-orange-600 dark:text-orange-400")}>{isDossier ? "Dossier" : "Étude"}</span></td>
                          <td className="px-2 py-2 text-[10px]"><span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold", eta === "rdv_admin" ? "bg-blue-500/20 text-blue-600" : "bg-muted/50 text-muted-foreground")}>{eta === "rdv_admin" ? "RDV" : eta?.substring(0, 12) || "-"}</span></td>
                          <td className="px-2 py-2 text-xs font-semibold truncate max-w-[100px]">{clientName || "-"}</td>
                          <td className="px-2 py-2 text-[10px] text-muted-foreground truncate">{clientPhone || "-"}</td>
                          <td className="px-2 py-2"><span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold uppercase", prog === "cumule" ? "bg-violet-500/20 text-violet-600" : prog === "174" ? "bg-emerald-500/20 text-emerald-600" : "bg-blue-500/20 text-blue-600")}>{prog === "cumule" ? "CUM" : prog || "-"}</span></td>
                          <td className="px-2 py-2 text-[10px]">{zone}</td>
                          <td className="px-2 py-2 text-[10px]">{surface || "-"}</td>
                          <td className="px-2 py-2"><span className={cn("w-4 h-4 rounded-full inline-flex items-center justify-center text-[8px] font-bold", precarite?.toLowerCase() === "bleu" ? "bg-blue-500 text-white" : "bg-yellow-400 text-black")}>{precarite?.charAt(0)?.toUpperCase() || "?"}</span></td>
                          <td className="px-2 py-2"><div className="flex items-center gap-1"><Progress value={stepsProgress} className="w-10 h-1.5" /><span className="text-[10px] font-medium">{stepsProgress}%</span></div></td>
                          <td className="px-2 py-2 text-[10px] font-semibold tabular-nums">{totalPay > 0 ? formatCurrency(totalPay) : "-"}</td>
                          {!isRestricted && <td className="px-2 py-2 text-[10px] font-semibold tabular-nums text-emerald-600">{marge ? formatCurrency(marge) : "-"}</td>}
                          <td className="px-1 py-2" onClick={e2 => e2.stopPropagation()}>
                            <button onClick={async () => {
                              if (!confirm(`Supprimer ${clientName || "ce dossier"} ?`)) return;
                              try {
                                await axios.delete(`${API}/dossiers/${e.id}`).catch(() => axios.delete(`${API}/etudes/${e.id}?role=admin_full`))
                                toast.success("Supprimé"); fetchAllData();
                              } catch (err) { toast.error("Erreur"); }
                            }} className="text-red-500 hover:text-red-700 hover:bg-red-500/10 p-1 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-border rounded-xl">
                <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun élément pour {ru.username}</p>
              </div>
            );
            })()}
          </TabsContent>
        ))}
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Supprimer le dossier ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le dossier et tous ses documents seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
        <DialogContent className="rounded-xl max-w-sm">
          <DialogHeader><DialogTitle>Attribuer {selectedDossiers.length} dossier(s)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={bulkAssignUser} onChange={e => setBulkAssignUser(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm">
              <option value="">Choisir un utilisateur</option>
              {usersList.filter(u => u.active !== false).map(u => <option key={u.username} value={u.username}>{u.username} ({u.role})</option>)}
            </select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowBulkAssign(false); setSelectedDossiers([]); }} className="flex-1 rounded-lg">Annuler</Button>
              <Button onClick={handleBulkAssign} disabled={savingBulk || !bulkAssignUser} className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-600 text-white">
                {savingBulk ? "..." : "Attribuer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
